import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronUp, Eye, EyeOff, GripVertical, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { ToothLogo } from "@/components/ToothLogo";
import ProfessionalForm from "@/admin/ProfessionalForm";
import ImageUploader from "@/admin/ImageUploader";

const TABS = [
  ["professionals", "Profesionales"],
  ["specialties", "Especialidades"],
  ["insurances", "Aseguradoras"],
  ["locations", "Ubicaciones"],
  ["banners", "Banners"],
  ["countries_popup", "Países popup"],
  ["plans", "Planes"],
  ["content", "Textos"],
];

const field = "w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm outline-none focus:border-neutral-900";
const lbl = "mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500";
const btnDark = "rounded-xl bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-neutral-800";

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("professionals");
  const [country, setCountry] = useState(localStorage.getItem("op_admin_country") || "EC");
  const [locations, setLocations] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [insurances, setInsurances] = useState([]);

  const changeCountry = (c) => {
    setCountry(c);
    localStorage.setItem("op_admin_country", c);
  };

  const loadShared = useCallback(() => {
    api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/specialties").then((r) => setSpecialties(r.data)).catch(() => {});
    api.get("/admin/insurances").then((r) => setInsurances(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadShared();
  }, [loadShared]);

  return (
    <div className="min-h-[100dvh] bg-neutral-50">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-neutral-200 bg-white px-5 py-4">
        <div className="flex items-center gap-2">
          <img
            src="/assets/logo.webp"
            alt="odontopick"
            className="h-5 w-auto object-contain"
          />
          <span className="ml-1 rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-500">admin</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-full bg-neutral-100 p-1" data-testid="admin-country-switch">
            {[["EC", "🇪🇨 Ecuador"], ["AR", "🇦🇷 Argentina"]].map(([c, label]) => (
              <button
                key={c}
                data-testid={`admin-country-${c}`}
                onClick={() => changeCountry(c)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                  country === c ? "bg-neutral-900 text-white" : "text-neutral-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button data-testid="logout-btn" onClick={onLogout} className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900">
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </header>

      <nav className="no-scrollbar flex gap-1 overflow-x-auto border-b border-neutral-200 bg-white px-4">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${
              tab === k ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-400"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      <main className="mx-auto max-w-5xl p-5">
        {tab === "professionals" && (
          <ProfessionalsTab country={country} locations={locations} specialties={specialties} insurances={insurances} />
        )}
        {tab === "specialties" && <SpecialtiesTab country={country} />}
        {tab === "insurances" && <InsurancesTab country={country} insurances={insurances} reload={loadShared} />}
        {tab === "locations" && <LocationsTab country={country} locations={locations} reload={loadShared} />}
        {tab === "banners" && <BannersTab country={country} />}
        {tab === "countries_popup" && <CountriesPopupTab />}
        {tab === "plans" && <PlansTab country={country} />}
        {tab === "content" && <ContentTab country={country} />}
      </main>
    </div>
  );
}

/* ---------------- Professionals ---------------- */
function ProfessionalsTab({ country, locations, specialties, insurances }) {
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [kind, setKind] = useState("all");
  const [sortBy, setSortBy] = useState("ranking"); // "ranking" | "views"
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // object | "new" | null

  const load = useCallback(() => {
    const params = new URLSearchParams({ country });
    if (kind !== "all") params.set("kind", kind);
    api.get(`/admin/professionals?${params.toString()}`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/admin/metrics?country=${country}`).then((r) => setMetrics(r.data)).catch(() => {});
  }, [kind, country]);
  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    let list = items;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) => p.name?.toLowerCase().includes(q));
    }
    if (sortBy === "views") return [...list].sort((a, b) => (b.views || 0) - (a.views || 0));
    return list;
  }, [items, sortBy, search]);

  const clinics = items.filter((i) => i.kind === "clinic");

  const toggleHidden = async (p) => {
    await api.put(`/admin/professionals/${p.id}`, { is_hidden: !p.is_hidden });
    load();
  };
  const del = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.name}?`)) return;
    await api.delete(`/admin/professionals/${p.id}`);
    toast.success("Eliminado");
    load();
  };

  const savePosition = async (p, val) => {
    const pos = Number(val);
    if (Number.isNaN(pos) || pos === p.position) return;
    await api.put(`/admin/professionals/${p.id}`, { position: pos });
    toast.success("Posición actualizada");
    load();
  };

  return (
    <div>
      {/* Metrics summary */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Perfiles totales", metrics?.total, "text-neutral-900"],
          ["Verificados", metrics?.verified, "text-emerald-600"],
          ["Vistas acumuladas", metrics?.views, "text-neutral-900"],
          ["Clics WhatsApp", metrics?.wa_clicks, "text-green-600"],
        ].map(([label, val, color]) => (
          <div key={label} data-testid={`metric-${label}`} className="rounded-2xl border border-neutral-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{label}</p>
            <p className={`mt-1 font-brand text-3xl font-semibold ${color}`}>{val ?? "—"}</p>
          </div>
        ))}
      </div>

      {/* Most visited ranking */}
      {metrics?.top?.length > 0 && (
        <div className="mb-5 rounded-2xl border border-neutral-200 bg-white p-4" data-testid="most-visited">
          <h3 className="mb-3 font-brand text-base font-semibold text-neutral-900">Perfiles más visitados</h3>
          <div className="space-y-1.5">
            {metrics.top.filter((t) => t.views > 0).length === 0 && (
              <p className="text-sm text-neutral-400">Aún no hay visitas registradas.</p>
            )}
            {metrics.top.filter((t) => t.views > 0).map((t, i) => (
              <div key={t.id} className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-neutral-50">
                <span className="w-5 text-sm font-semibold text-neutral-400">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-neutral-900">
                    {t.name}
                    {t.plan === "verified" && <span className="ml-1 text-emerald-600">✓</span>}
                  </p>
                  <p className="text-xs text-neutral-400">{t.kind === "clinic" ? "Clínica" : "Odontólogo"}{t.city ? ` · ${t.city}` : ""}</p>
                </div>
                <span className="shrink-0 rounded-full bg-neutral-100 px-3 py-1 text-sm font-semibold text-neutral-800">{t.views} vistas · {t.wa_clicks || 0} WhatsApp</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {metrics?.wa_geo && (
        <div className="mb-5 rounded-2xl border border-neutral-200 bg-white p-4" data-testid="wa-geo">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-brand text-base font-semibold text-neutral-900">Procedencia de clics de WhatsApp</h3>
            <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">{metrics.wa_geo.total} clics ubicados</span>
          </div>
          {metrics.wa_geo.total === 0 ? (
            <p className="text-sm text-neutral-400">Aún no hay clics de WhatsApp geolocalizados. Aparecerán aquí cuando los visitantes toquen el botón de WhatsApp.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                ["País", metrics.wa_geo.by_country],
                ["Provincia", metrics.wa_geo.by_province],
                ["Ciudad", metrics.wa_geo.by_city],
              ].map(([label, rows]) => {
                const max = Math.max(1, ...(rows || []).map((r) => r.count));
                return (
                  <div key={label} data-testid={`wa-geo-${label}`}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">{label}</p>
                    <div className="space-y-2">
                      {(rows || []).map((r, idx) => (
                        <div key={r.name}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="truncate text-neutral-800">
                              <span className="mr-1.5 font-semibold text-neutral-400">{idx + 1}.</span>
                              {r.name}
                            </span>
                            <span className="ml-2 shrink-0 font-semibold text-neutral-500">{r.count}</span>
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-neutral-100">
                            <div className="h-full rounded-full bg-green-500" style={{ width: `${(r.count / max) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}


      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-xl bg-neutral-100 p-1">
            {[["all", "Todos"], ["doctor", "Odontólogos"], ["clinic", "Clínicas"]].map(([k, l]) => (
              <button key={k} onClick={() => setKind(k)} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${kind === k ? "bg-white shadow-sm" : "text-neutral-500"}`}>
                {l}
              </button>
            ))}
          </div>
          <input
            data-testid="professional-search"
            className="w-56 rounded-xl border border-neutral-200 px-3 py-2.5 text-sm outline-none focus:border-neutral-900"
            placeholder="Buscar por nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="sort-views-btn"
            onClick={() => setSortBy((s) => (s === "views" ? "ranking" : "views"))}
            className={`rounded-xl border px-3 py-2.5 text-sm font-medium ${
              sortBy === "views" ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 text-neutral-600"
            }`}
          >
            {sortBy === "views" ? "Más vistos ✓" : "Ordenar por vistas"}
          </button>
          <button data-testid="new-professional-btn" onClick={() => setEditing("new")} className={`flex items-center gap-2 ${btnDark}`}>
            <Plus className="h-4 w-4" /> Nuevo
          </button>
        </div>
      </div>
      <p className="mb-3 text-xs text-neutral-400">Tip: escribe el número en la columna <b>Pos.</b> y presiona Enter para reordenar (menor = aparece primero).</p>

      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase text-neutral-400">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Ubicación</th>
              <th className="px-4 py-3">Pos.</th>
              <th className="px-4 py-3">Vistas</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid={`row-${p.id}`} className="border-b border-neutral-100 last:border-0">
                <td className="px-4 py-3 font-medium text-neutral-900">
                  {p.name} {p.is_hidden && <span className="ml-1 text-xs text-neutral-400">(oculto)</span>}
                </td>
                <td className="px-4 py-3 text-neutral-500">{p.kind === "clinic" ? "Clínica" : "Odontólogo"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.plan === "verified" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-500"}`}>
                    {p.plan === "verified" ? "Verificado" : "Free"}
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-500">{p.city}, {p.country}</td>
                <td className="px-2 py-3">
                  <input
                    type="number"
                    data-testid={`position-${p.id}`}
                    defaultValue={p.position}
                    key={`${p.id}-${p.position}`}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    onBlur={(e) => savePosition(p, e.target.value)}
                    className="w-16 rounded-lg border border-neutral-200 px-2 py-1.5 text-sm outline-none focus:border-neutral-900"
                  />
                </td>
                <td className="px-4 py-3 font-medium text-neutral-700" data-testid={`views-${p.id}`}>{p.views || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button title="Editar" data-testid={`edit-${p.id}`} onClick={() => setEditing(p)} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
                    <button title="Ocultar" data-testid={`hide-${p.id}`} onClick={() => toggleHidden(p)} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-neutral-100">{p.is_hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    <button title="Eliminar" data-testid={`del-${p.id}`} onClick={() => del(p)} className="grid h-8 w-8 place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">Sin registros</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProfessionalForm
          initial={editing === "new" ? { country } : editing}
          locations={locations}
          specialties={specialties}
          insurances={insurances}
          clinics={clinics}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ---------------- Specialties ---------------- */
function SpecialtiesTab({ country }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/specialties?country=${country}`).then((r) => setItems(r.data)).catch(() => {});
  }, [country]);
  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/admin/specialties", { name: name.trim(), country });
    setName("");
    toast.success("Especialidad agregada");
    load();
  };
  const del = async (s) => {
    await api.delete(`/admin/specialties/${s.id}`);
    load();
  };

  const setColor = async (s, color) => {
    setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, color } : x)));
    await api.put(`/admin/specialties/${s.id}`, { color });
  };

  const move = async (s, dir) => {
    const idx = items.findIndex((x) => x.id === s.id);
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= items.length) return;
    const n = items[j];
    const so = s.order ?? idx;
    const no = n.order ?? j;
    let ns = no, nn = so;
    if (so === no) {
      ns = dir === "up" ? no - 1 : no + 1;
      nn = no;
    }
    await Promise.all([
      api.put(`/admin/specialties/${s.id}`, { order: ns }),
      api.put(`/admin/specialties/${n.id}`, { order: nn }),
    ]);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex-1">
          <span className={lbl}>Especialidad ({country === "EC" ? "Ecuador 🇪🇨" : "Argentina 🇦🇷"})</span>
          <input
            data-testid="specialty-name"
            className={field}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Ej. Ortodoncia"
          />
        </div>
        <button data-testid="add-specialty-btn" onClick={add} className={btnDark}>Agregar</button>
      </div>
      <p className="text-xs text-neutral-400">Usa las flechas para ordenar y el selector de color para el trazo de cada especialidad en la pantalla de resultados.</p>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div key={s.id} className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-4 py-2.5">
            <span className="text-sm font-medium">{i + 1}. {s.name}</span>
            <div className="flex items-center gap-1">
              <label
                data-testid={`spec-color-label-${s.id}`}
                className="mr-1 flex items-center gap-1.5 rounded-lg border border-neutral-200 px-2 py-1"
                title="Color del trazo"
              >
                <span className="h-4 w-4 rounded-full ring-1 ring-neutral-300" style={{ backgroundColor: s.color || "#ffffff" }} />
                <input
                  data-testid={`spec-color-${s.id}`}
                  type="color"
                  value={s.color || "#ffffff"}
                  onChange={(e) => setColor(s, e.target.value)}
                  className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0"
                />
              </label>
              <button data-testid={`spec-up-${s.id}`} disabled={i === 0} onClick={() => move(s, "up")} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-neutral-100 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <button data-testid={`spec-down-${s.id}`} disabled={i === items.length - 1} onClick={() => move(s, "down")} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-neutral-100 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
              <button data-testid={`del-spec-${s.id}`} onClick={() => del(s)} className="grid h-8 w-8 place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-neutral-400">Sin especialidades en este país.</p>}
      </div>
    </div>
  );
}

/* ---------------- Insurances ---------------- */
function InsurancesTab({ country, insurances, reload }) {
  const [name, setName] = useState("");
  const list = insurances.filter((i) => i.country === country);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/admin/insurances", { name: name.trim(), country });
    setName("");
    toast.success("Aseguradora agregada");
    reload();
  };
  const del = async (i) => { await api.delete(`/admin/insurances/${i.id}`); reload(); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex-1"><span className={lbl}>Nombre ({country === "EC" ? "Ecuador" : "Argentina"})</span><input data-testid="insurance-name" className={field} value={name} onChange={(e) => setName(e.target.value)} /></div>
        <button data-testid="add-insurance-btn" onClick={add} className={btnDark}>Agregar</button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {list.map((i) => (
          <div key={i.id} className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-4 py-3">
            <span className="text-sm"><b>{i.name}</b></span>
            <button data-testid={`del-ins-${i.id}`} onClick={() => del(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-neutral-400">Sin aseguradoras en este país.</p>}
      </div>
    </div>
  );
}

/* ---------------- Locations ---------------- */
function LocationsTab({ country, locations, reload }) {
  const [province, setProvince] = useState("");
  const [cities, setCities] = useState("");
  const list = locations.filter((l) => l.country === country);

  const add = async () => {
    if (!province.trim()) return;
    await api.post("/admin/locations", {
      country, province: province.trim(),
      cities: cities.split(",").map((c) => c.trim()).filter(Boolean),
    });
    setProvince(""); setCities("");
    toast.success("Provincia agregada");
    reload();
  };
  const del = async (l) => { await api.delete(`/admin/locations/${l.id}`); reload(); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex-1"><span className={lbl}>Provincia ({country === "EC" ? "Ecuador" : "Argentina"})</span><input data-testid="loc-province" className={field} value={province} onChange={(e) => setProvince(e.target.value)} /></div>
        <div className="flex-1"><span className={lbl}>Ciudades (separadas por coma)</span><input data-testid="loc-cities" className={field} value={cities} onChange={(e) => setCities(e.target.value)} /></div>
        <button data-testid="add-location-btn" onClick={add} className={btnDark}>Agregar</button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {list.map((l) => (
          <LocationCard key={l.id} l={l} reload={reload} onDelete={() => del(l)} />
        ))}
        {list.length === 0 && <p className="text-sm text-neutral-400">Sin provincias en este país.</p>}
      </div>
    </div>
  );
}

function ZoneEditor({ zones, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    const z = val.trim();
    if (!z || zones.includes(z)) return;
    onChange([...zones, z]);
    setVal("");
  };
  const remove = (z) => onChange(zones.filter((x) => x !== z));
  return (
    <div data-testid={testid}>
      {zones.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {zones.map((z) => (
            <span key={z} className="inline-flex items-center gap-1 rounded-full bg-neutral-200 px-2.5 py-1 text-xs text-neutral-800">
              {z}
              <button data-testid={`${testid}-del-${z}`} onClick={() => remove(z)} className="text-neutral-500 hover:text-red-500" title="Eliminar zona">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          data-testid={`${testid}-input`}
          className="min-w-0 flex-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm outline-none focus:border-neutral-900"
          placeholder="Agregar zona"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button data-testid={`${testid}-add`} onClick={add} className="flex shrink-0 items-center gap-1 rounded-lg bg-neutral-700 px-3 py-1.5 text-xs font-medium text-white">
          <Plus className="h-3.5 w-3.5" /> Zona
        </button>
      </div>
    </div>
  );
}

function LocationCard({ l, reload, onDelete }) {
  const [newCity, setNewCity] = useState("");

  const addCity = async () => {
    const c = newCity.trim();
    if (!c || (l.cities || []).includes(c)) return;
    await api.put(`/admin/locations/${l.id}`, { cities: [...(l.cities || []), c] });
    setNewCity("");
    toast.success(`Ciudad "${c}" agregada`);
    reload();
  };

  const delCity = async (city) => {
    const cities = (l.cities || []).filter((x) => x !== city);
    const city_zones = { ...(l.city_zones || {}) };
    delete city_zones[city];
    await api.put(`/admin/locations/${l.id}`, { cities, city_zones });
    toast.success(`Ciudad "${city}" eliminada`);
    reload();
  };

  const saveZones = async (city, arr) => {
    const city_zones = { ...(l.city_zones || {}) };
    if (arr.length) city_zones[city] = arr;
    else delete city_zones[city];
    await api.put(`/admin/locations/${l.id}`, { city_zones });
    reload();
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4" data-testid={`loc-card-${l.id}`}>
      <div className="mb-3 flex items-center justify-between">
        <b className="text-sm">{l.province}</b>
        <button data-testid={`loc-delete-${l.id}`} onClick={onDelete} className="text-red-500" title="Eliminar provincia"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="space-y-2">
        {(l.cities || []).map((c) => (
          <div key={c} className="rounded-lg bg-neutral-50 p-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-medium text-neutral-800">{c}</span>
              <button data-testid={`loc-city-delete-${l.id}-${c}`} onClick={() => delCity(c)} className="text-red-400 hover:text-red-600" title="Eliminar ciudad">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <ZoneEditor zones={(l.city_zones || {})[c] || []} onChange={(arr) => saveZones(c, arr)} testid={`loc-zones-${l.id}-${c}`} />
          </div>
        ))}
        {(l.cities || []).length === 0 && <p className="text-xs text-neutral-400">Sin ciudades.</p>}
      </div>
      <div className="mt-3 flex items-center gap-2 border-t border-neutral-100 pt-3">
        <input
          data-testid={`loc-add-city-input-${l.id}`}
          className="min-w-0 flex-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm outline-none focus:border-neutral-900"
          placeholder="Agregar ciudad a esta provincia"
          value={newCity}
          onChange={(e) => setNewCity(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addCity()}
        />
        <button data-testid={`loc-add-city-btn-${l.id}`} onClick={addCity} className="flex shrink-0 items-center gap-1 rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white">
          <Plus className="h-3.5 w-3.5" /> Ciudad
        </button>
      </div>
    </div>
  );
}

/* ---------------- Países popup (Próximamente) ---------------- */
function CountriesPopupTab() {
  const [popups, setPopups] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/config?country=EC")
      .then((r) => { setPopups(r.data.country_popups || {}); })
      .finally(() => setLoaded(true));
  }, []);

  const save = async (next) => {
    setPopups(next);
    await api.put("/admin/config", { country_popups: next });
    toast.success("Imagen guardada");
  };

  const countries = [["PE", "Perú"], ["CO", "Colombia"], ["UY", "Uruguay"], ["CL", "Chile"], ["PY", "Paraguay"]];
  if (!loaded) return null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-500">Imagen del popup “Próximamente” para cada país (una imagen vertical por país).</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {countries.map(([code, name]) => (
          <div key={code} className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`popup-card-${code}`}>
            <div className="mb-2 text-sm font-semibold">{name}</div>
            <ImageUploader
              value={popups[code] ? [popups[code]] : []}
              onChange={(v) => save({ ...popups, [code]: v[0] || "" })}
              single
              testid={`popup-img-${code}`}
              hint="Imagen vertical (póster)."
            />
            <span className="mt-1 block text-[10px] leading-tight text-neutral-400" data-testid={`popup-size-hint-${code}`}>Medida exacta: 1080 × 1440 px (3:4 vertical)</span>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ---------------- Banners ---------------- */
function BannersTab({ country }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get(`/admin/banners?country=${country}`).then((r) => setItems(r.data)).catch(() => {});
  }, [country]);
  useEffect(() => {
    load();
  }, [load]);

  const blank = { image: "", title: "", subtitle: "", link: "", order: 0, active: true, country };
  const [draft, setDraft] = useState(null);

  const save = async () => {
    const payload = { ...draft, country: draft.country || country };
    if (draft.id) await api.put(`/admin/banners/${draft.id}`, payload);
    else await api.post("/admin/banners", payload);
    toast.success("Banner guardado");
    setDraft(null); load();
  };
  const del = async (b) => { await api.delete(`/admin/banners/${b.id}`); load(); };

  const atMax = items.length >= 4;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          data-testid="new-banner-btn"
          onClick={() => !atMax && setDraft(blank)}
          disabled={atMax}
          className={`flex items-center gap-2 ${btnDark} ${atMax ? "opacity-40" : ""}`}
        >
          <Plus className="h-4 w-4" /> Nuevo banner
        </button>
        <span className={`text-xs ${atMax ? "font-semibold text-red-500" : "text-neutral-400"}`}>
          {items.length}/4 banners{atMax ? " · máximo alcanzado" : ""}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((b) => (
          <div key={b.id} className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {b.image && <img src={fileUrl(b.image)} alt="" className="h-28 w-full object-cover" />}
            <div className="flex items-center justify-between p-3">
              <div><b className="text-sm">Banner{b.order ? ` · orden ${b.order}` : ""}</b><div className="text-xs text-neutral-400">{b.link || "sin enlace"}</div></div>
              <div className="flex gap-1">
                <button data-testid={`edit-banner-${b.id}`} onClick={() => setDraft(b)} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(b)} className="grid h-8 w-8 place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDraft(null)}>
          <div className="w-full max-w-lg space-y-4 rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="banner-form">
            <h2 className="font-brand text-lg font-semibold">{draft.id ? "Editar" : "Nuevo"} banner</h2>
            <div><span className={lbl}>Imagen del banner</span><ImageUploader value={draft.image ? [draft.image] : []} onChange={(v) => setDraft({ ...draft, image: v[0] || "" })} single testid="banner-img" hint="Se recorta al centro; pon lo importante centrado." /><span className="mt-1 block text-[10px] leading-tight text-neutral-400" data-testid="banner-size-hint">Medida exacta: 1200 × 235 px (proporción ~5:1)</span></div>
            <div><span className={lbl}>Enlace (opcional)</span><input className={field} value={draft.link} onChange={(e) => setDraft({ ...draft, link: e.target.value })} placeholder="https://..." /></div>
            <div className="flex items-center gap-4">
              <div><span className={lbl}>Orden</span><input type="number" className={field} value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} /></div>
              <label className="flex items-center gap-2 pt-4 text-sm"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Activo</label>
            </div>
            <div className="flex justify-end gap-3"><button onClick={() => setDraft(null)} className="px-4 py-2 text-neutral-600">Cancelar</button><button data-testid="save-banner-btn" onClick={save} className={btnDark}>Guardar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Plans ---------------- */
function FeatureList({ value, onChange, testid }) {
  const items = value || [];
  const update = (i, val) => onChange(items.map((x, idx) => (idx === i ? val : x)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...items, ""]);
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-2.5 py-1.5">
          <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-green-500 text-white"><Check className="h-3 w-3" strokeWidth={3} /></span>
          <input
            data-testid={`${testid}-input-${i}`}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
            placeholder="Escribe una especificación…"
            value={it}
            onChange={(e) => update(i, e.target.value)}
          />
          <div className="flex shrink-0 items-center gap-0.5">
            <button data-testid={`${testid}-up-${i}`} disabled={i === 0} onClick={() => move(i, "up")} className="grid h-7 w-7 place-items-center rounded-lg hover:bg-neutral-200 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
            <button data-testid={`${testid}-down-${i}`} disabled={i === items.length - 1} onClick={() => move(i, "down")} className="grid h-7 w-7 place-items-center rounded-lg hover:bg-neutral-200 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
            <button data-testid={`${testid}-del-${i}`} onClick={() => remove(i)} className="grid h-7 w-7 place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      <button data-testid={`${testid}-add`} onClick={add} className="flex items-center gap-1 text-sm font-medium text-neutral-700"><Plus className="h-4 w-4" /> Agregar especificación</button>
    </div>
  );
}

function PlansTab({ country }) {
  const [p, setP] = useState(null);
  const isAR = country === "AR";
  useEffect(() => {
    setP(null);
    api.get(`/config?country=${country}`).then((r) => setP(r.data.plans || defaultPlans(isAR))).catch(() => setP(defaultPlans(isAR)));
  }, [country, isAR]);
  if (!p) return <div className="text-neutral-400">Cargando…</div>;

  const v = p.verified;
  const fr = p.free;
  const setV = (patch) => setP({ ...p, verified: { ...v, ...patch } });
  const setFr = (patch) => setP({ ...p, free: { ...fr, ...patch } });

  const updatePrice = (i, key, val) => {
    const prices = [...(v.prices || [])];
    prices[i] = { ...prices[i], [key]: val };
    setV({ prices });
  };
  const addPrice = () => setV({ prices: [...(v.prices || []), { label: "", usd: "", ...(isAR ? { ars: "" } : {}) }] });
  const removePrice = (i) => setV({ prices: v.prices.filter((_, idx) => idx !== i) });

  const toNum = (x) => {
    const n = parseFloat(String(x ?? "").replace(",", ".").trim());
    return isNaN(n) ? 0 : n;
  };

  const save = async () => {
    const clean = (arr) => (arr || []).map((s) => (s || "").trim()).filter(Boolean);
    const prices = (v.prices || []).map((pr) => ({
      ...pr,
      usd: toNum(pr.usd),
      ...(pr.ars !== undefined ? { ars: toNum(pr.ars) } : {}),
    }));
    const plans = {
      ...p,
      verified: { ...v, currencies: isAR ? ["USD", "ARS"] : ["USD"], prices, features: clean(v.features) },
      free: { ...fr, features: clean(fr.features) },
    };
    await api.put(`/admin/config?country=${country}`, { plans });
    toast.success("Planes guardados");
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-xl bg-neutral-100 px-4 py-2 text-sm text-neutral-600">
        Editando planes de <b>{isAR ? "Argentina 🇦🇷" : "Ecuador 🇪🇨"}</b>
        {isAR && " · precios en USD y pesos argentinos (ARS)"}
      </div>
      {/* Verified */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-5">
        <h3 className="mb-3 font-brand text-lg font-semibold">Plan Verificado</h3>
        <div className="mb-4">
          <span className={lbl}>Nombre</span>
          <input className={field} value={v.name || ""} onChange={(e) => setV({ name: e.target.value })} />
        </div>
        <span className={lbl}>Precios</span>
        <div className="mb-3 space-y-2">
          {(v.prices || []).map((pr, i) => (
            <div key={i} className="flex items-center gap-2">
              <input data-testid={`plan-price-label-${i}`} className={field} placeholder="Ej. 6 meses" value={pr.label} onChange={(e) => updatePrice(i, "label", e.target.value)} />
              <div className="flex items-center gap-1">
                <span className="text-xs text-neutral-400">USD $</span>
                <input data-testid={`plan-price-usd-${i}`} type="text" inputMode="decimal" placeholder="0.00" className={`${field} w-24`} value={pr.usd ?? ""} onChange={(e) => updatePrice(i, "usd", e.target.value)} />
              </div>
              {isAR && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-neutral-400">ARS $</span>
                  <input data-testid={`plan-price-ars-${i}`} type="text" inputMode="decimal" placeholder="0.00" className={`${field} w-28`} value={pr.ars ?? ""} onChange={(e) => updatePrice(i, "ars", e.target.value)} />
                </div>
              )}
              <button onClick={() => removePrice(i)} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button data-testid="add-price-btn" onClick={addPrice} className="flex items-center gap-1 text-sm font-medium text-neutral-700"><Plus className="h-4 w-4" /> Agregar precio</button>
        </div>
        <span className={lbl}>Especificaciones (ordena con las flechas)</span>
        <FeatureList
          testid="plan-verified-features"
          value={v.features || []}
          onChange={(features) => setV({ features })}
        />
      </div>

      {/* Free */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-5">
        <h3 className="mb-3 font-brand text-lg font-semibold">Plan Free</h3>
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <span className={lbl}>Nombre</span>
            <input className={field} value={fr.name || ""} onChange={(e) => setFr({ name: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>Etiqueta de precio</span>
            <input className={field} value={fr.price_label || ""} onChange={(e) => setFr({ price_label: e.target.value })} />
          </div>
        </div>
        <span className={lbl}>Especificaciones (ordena con las flechas)</span>
        <FeatureList
          testid="plan-free-features"
          value={fr.features || []}
          onChange={(features) => setFr({ features })}
        />
      </div>

      <button data-testid="save-plans-btn" onClick={save} className={btnDark}>Guardar planes</button>
    </div>
  );
}

function defaultPlans(isAR) {
  const prices = [
    { label: "6 meses", usd: 60, ...(isAR ? { ars: 90000 } : {}) },
    { label: "12 meses", usd: 72, ...(isAR ? { ars: 108000 } : {}) },
  ];
  return {
    verified: { name: "Plan Verificado", currencies: isAR ? ["USD", "ARS"] : ["USD"], prices, features: [] },
    free: { name: "Plan Free", price_label: "Gratis", features: [] },
  };
}

/* ---------------- Content ---------------- */
function ContentTab({ country }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    setC(null);
    api.get(`/config?country=${country}`).then((r) => setC(r.data)).catch(() => setC({}));
  }, [country]);
  if (!c) return <div className="text-neutral-400">Cargando…</div>;
  const set = (k, v) => setC({ ...c, [k]: v });
  const save = async () => {
    // global fields (marca, fondo y datos de contacto compartidos por ambos países)
    await api.put("/admin/config", {
      brand: c.brand,
      background_image: c.background_image,
      contact: c.contact,
      contact_whatsapp: c.contact_whatsapp,
    });
    // per-country fields (textos legales distintos por país)
    await api.put(`/admin/config?country=${country}`, {
      hero_cta: c.hero_cta,
      advertise_text: c.advertise_text,
      advertise_link: c.advertise_link,
      terms: c.terms,
      privacy: c.privacy,
    });
    toast.success("Textos guardados");
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-xl bg-neutral-100 px-4 py-2 text-sm text-neutral-600">
        Editando textos de <b>{country === "AR" ? "Argentina 🇦🇷" : "Ecuador 🇪🇨"}</b> (marca y fondo son globales)
      </div>
      <div><span className={lbl}>Nombre de marca (global)</span><input className={field} value={c.brand || ""} onChange={(e) => set("brand", e.target.value)} /></div>
      <div><span className={lbl}>Texto botón principal</span><input data-testid="content-hero" className={field} value={c.hero_cta || ""} onChange={(e) => set("hero_cta", e.target.value)} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><span className={lbl}>Texto "Anúnciate"</span><input className={field} value={c.advertise_text || ""} onChange={(e) => set("advertise_text", e.target.value)} /></div>
        <div><span className={lbl}>WhatsApp / Enlace "Anúnciate"</span><input className={field} value={c.advertise_link || ""} onChange={(e) => set("advertise_link", e.target.value)} /></div>
      </div>
      <div><span className={lbl}>Imagen de fondo (global)</span><ImageUploader value={c.background_image ? [c.background_image] : []} onChange={(v) => set("background_image", v[0] || "")} single testid="bg-img" hint="Vertical 9:16 (ej. 1080×1920 px). Máx HD." /></div>
      <div><span className={lbl}>Términos y condiciones <b className="text-neutral-400">({country === "AR" ? "solo Argentina 🇦🇷" : "solo Ecuador 🇪🇨"})</b></span><textarea data-testid="content-terms" className={field} rows={5} value={c.terms || ""} onChange={(e) => set("terms", e.target.value)} /></div>
      <div><span className={lbl}>Política de privacidad <b className="text-neutral-400">({country === "AR" ? "solo Argentina 🇦🇷" : "solo Ecuador 🇪🇨"})</b></span><textarea data-testid="content-privacy" className={field} rows={5} value={c.privacy || ""} onChange={(e) => set("privacy", e.target.value)} /></div>
      <div><span className={lbl}>Contáctanos (texto) <b className="text-neutral-400">(compartido EC/AR)</b></span><textarea className={field} rows={2} value={c.contact || ""} onChange={(e) => set("contact", e.target.value)} /></div>
      <div><span className={lbl}>WhatsApp de contacto <b className="text-neutral-400">(compartido EC/AR)</b></span><input data-testid="content-contact-wa" className={field} placeholder="Ej: +593 99 123 4567" value={c.contact_whatsapp || ""} onChange={(e) => set("contact_whatsapp", e.target.value)} /><span className="mt-1 block text-xs text-neutral-400">Se muestra como botón en "Contáctanos" y abre WhatsApp.</span></div>
      <button data-testid="save-content-btn" onClick={save} className={btnDark}>Guardar textos</button>
    </div>
  );
}
